import React, { useEffect, useState } from "react";
import { Film, Play } from "lucide-react";
import { api } from "../api";

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;
    api
      .get("/projects")
      .then((r) => mounted && setProjects(r.data))
      .catch((e) =>
        mounted && setError(e?.response?.data?.detail || "Could not load projects")
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#121212] pt-32 pb-24" data-testid="projects-page">
      <div className="max-w-7xl mx-auto px-6">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">
            Featured Work
          </span>
        </div>

        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <h1 className="font-display font-light text-5xl md:text-6xl text-white leading-tight">
            Projects
          </h1>
          <p className="font-body text-white/40 text-sm max-w-md md:text-right">
            A curated selection of stage productions, choreography reels and
            cinematic moments — straight from the MRVA roster.
          </p>
        </div>

        <div className="gold-divider mb-14" />

        {/* States */}
        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3">
            <p className="font-body text-red-400 text-sm">{error}</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="glass rounded-sm py-24 text-center">
            <Film size={42} className="text-white/10 mx-auto mb-4" />
            <p className="font-body text-white/30 text-sm">
              No projects yet. Check back soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((p, idx) => (
              <article
                key={p.id}
                className="group relative"
                data-testid={`project-card-${p.id}`}
              >
                {/* Index */}
                <div className="absolute -top-3 left-0 z-10 flex items-center gap-2">
                  <span
                    className="font-body text-gold/70 text-[0.55rem] tracking-[0.35em] uppercase font-semibold bg-[#121212] px-2 py-1"
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Video */}
                <div
                  className="relative w-full overflow-hidden rounded-sm bg-black border border-gold/15 group-hover:border-gold/40 transition-colors"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${p.youtubeId}?rel=0&modestbranding=1`}
                    title={p.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    data-testid={`project-iframe-${p.id}`}
                  />
                </div>

                {/* Meta */}
                <div className="pt-5">
                  <h2 className="font-display text-2xl text-white leading-tight mb-2 group-hover:text-gold transition-colors">
                    {p.title}
                  </h2>
                  {p.description && (
                    <p className="font-body text-white/40 text-sm leading-relaxed">
                      {p.description}
                    </p>
                  )}
                  <a
                    href={p.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 font-body text-gold/70 hover:text-gold text-[0.65rem] tracking-[0.3em] uppercase transition-colors"
                    data-testid={`project-watch-${p.id}`}
                  >
                    <Play size={11} />
                    <span>Watch on YouTube</span>
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
